<template>
  <div class="group">
    <article :id="'block-card-' + block.key" tabindex="-1" :aria-label="cardLabel"
             class="border rounded-md bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue"
             :class="error ? 'border-red-300' : 'border-gray-200'">
      <header class="flex items-center gap-1 px-2 py-1.5 bg-gray-50 rounded-t-md"
              :class="collapsed ? 'rounded-b-md' : 'border-b border-gray-200'">
        <span class="block-drag-handle flex h-8 w-8 items-center justify-center cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700"
              :title="$t('projects.drag_block')" aria-hidden="true">
          <font-awesome-icon icon="grip-lines" class="h-4 w-4"/>
        </span>
        <button type="button" v-on:click="$emit('toggle-collapse')"
                :aria-expanded="collapsed ? 'false' : 'true'" :aria-controls="bodyId"
                :aria-label="$t(collapsed ? 'projects.expand_block' : 'projects.collapse_block')"
                :title="$t(collapsed ? 'projects.expand_block' : 'projects.collapse_block')"
                class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue">
          <font-awesome-icon :icon="collapsed ? 'chevron-right' : 'chevron-down'" class="h-3.5 w-3.5" aria-hidden="true"/>
        </button>

        <h3 class="flex min-w-0 flex-1 items-baseline gap-2 text-sm">
          <span class="font-semibold tabular-nums text-gray-600" aria-hidden="true">{{ index + 1 }}</span>
          <span class="font-semibold text-gray-800">{{ typeLabel }}</span>
          <span v-if="collapsed && summary" class="truncate text-gray-600">{{ summary }}</span>
          <span class="sr-only">{{ $t('projects.block_position', {position: index + 1, total}) }}</span>
        </h3>

        <button type="button" v-on:click="$emit('move', index - 1)" :disabled="saving || index === 0"
                :title="$t('projects.move_block_up')" :aria-label="$t('projects.move_block_up')"
                class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
          <font-awesome-icon icon="chevron-up" class="h-3.5 w-3.5" aria-hidden="true"/>
        </button>
        <button type="button" v-on:click="$emit('move', index + 1)" :disabled="saving || index === total - 1"
                :title="$t('projects.move_block_down')" :aria-label="$t('projects.move_block_down')"
                class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
          <font-awesome-icon icon="chevron-down" class="h-3.5 w-3.5" aria-hidden="true"/>
        </button>
        <action-menu-component :items="menuItems" :label="$t('projects.block_actions')" :disabled="saving"
                               @select="onAction"></action-menu-component>
      </header>

      <div v-show="!collapsed" :id="bodyId" class="p-3">
        <component v-if="blockType" :is="blockType.component" :model-value="block.data"
                   @update:model-value="$emit('update:data', $event)"
                   :block-id="'block-' + block.key" :lesson-uuid="lessonUuid"></component>
        <p v-else class="text-sm text-gray-600">{{ $t('projects.block_type_not_editable') }}</p>
      </div>
      <p v-if="error" class="px-3 pb-3 text-sm text-red-600" :class="{'pt-3': collapsed}" role="alert">
        {{ error.message ?? $t(error.key, error.params ?? {}) }}
      </p>
    </article>

    <!-- Inserción entre bloques: visible al pasar o con foco; siempre en pantallas táctiles. -->
    <div v-if="index < total - 1" class="relative flex h-8 items-center justify-center md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 motion-safe:transition-opacity">
      <span class="absolute inset-x-0 top-1/2 border-t border-dashed border-gray-300" aria-hidden="true"></span>
      <block-insert-menu class="relative" :block-types="blockTypes" :disabled="saving" compact
                         :label="$t('projects.insert_block_after', {position: index + 1})"
                         @select="$emit('insert-after', $event)"></block-insert-menu>
    </div>
    <div v-else class="h-4" aria-hidden="true"></div>
  </div>
</template>

<script>
import ActionMenuComponent from "@/components/menus/ActionMenuComponent";
import BlockInsertMenu from "@/views/projects/ProjectEdit/Blocks/BlockInsertMenu";

/*
 * Tarjeta de un bloque en el editor de lecciones (FE-052): cabecera con número,
 * tipo y resumen al plegar, subir/bajar y menú con duplicar, mover al principio
 * o al final y quitar. No muta el bloque: emite los cambios al editor.
 */
export default {
  name: "LessonBlockCard",
  components: {
    ActionMenuComponent,
    BlockInsertMenu
  },
  props: {
    block: {
      type: Object,
      required: true
    },
    blockTypes: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    lessonUuid: {
      type: String,
      required: true
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: null
    }
  },
  emits: ['update:data', 'move', 'duplicate', 'remove', 'toggle-collapse', 'insert-after'],
  computed: {
    blockType() {
      return this.blockTypes[this.block.type] ?? null
    },
    typeLabel() {
      return this.blockType ? this.$t(this.blockType.labelKey) : this.block.type
    },
    summary() {
      return this.blockType?.summary?.(this.block.data) ?? ''
    },
    bodyId() {
      return 'block-body-' + this.block.key
    },
    cardLabel() {
      return this.$t('projects.block_position', {position: this.index + 1, total: this.total}) + ': ' + this.typeLabel
    },
    menuItems() {
      return [
        {key: 'duplicate', label: this.$t('projects.duplicate_block'), icon: 'copy',
          disabled: !this.blockType || this.blockType.duplicable === false},
        {key: 'first', label: this.$t('projects.move_block_first'), icon: 'angle-double-up', disabled: this.index === 0},
        {key: 'last', label: this.$t('projects.move_block_last'), icon: 'angle-double-down', disabled: this.index === this.total - 1},
        {key: 'remove', label: this.$t('projects.delete_block'), icon: 'trash', danger: true},
      ]
    }
  },
  methods: {
    onAction(action) {
      if (action === 'duplicate') this.$emit('duplicate')
      if (action === 'first') this.$emit('move', 0)
      if (action === 'last') this.$emit('move', this.total - 1)
      if (action === 'remove') this.$emit('remove')
    }
  }
}
</script>
